import type { ImgHTMLAttributes } from "react";

/**
 * Serves pre-generated AVIF/WebP siblings for any image in src/assets,
 * falling back to the original JPG/PNG for older browsers.
 */
const originals = import.meta.glob("/src/assets/**/*.{jpg,jpeg,png}", {
  query: "?url",
  import: "default",
  eager: true,
}) as Record<string, string>;

const avifs = import.meta.glob("/src/assets/**/*.avif", {
  query: "?url",
  import: "default",
  eager: true,
}) as Record<string, string>;

const webps = import.meta.glob("/src/assets/**/*.webp", {
  query: "?url",
  import: "default",
  eager: true,
}) as Record<string, string>;

const byUrl = new Map<string, { avif?: string; webp?: string }>();
for (const [path, url] of Object.entries(originals)) {
  const stem = path.replace(/\.(jpe?g|png)$/i, "");
  byUrl.set(url, { avif: avifs[`${stem}.avif`], webp: webps[`${stem}.webp`] });
}

export function variantsFor(src: string) {
  return byUrl.get(src) ?? {};
}

type Props = ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  alt: string;
  pictureClassName?: string;
};

export function Picture({ src, alt, pictureClassName, ...imgProps }: Props) {
  const v = variantsFor(src);
  return (
    <picture className={pictureClassName}>
      {v.avif ? <source srcSet={v.avif} type="image/avif" /> : null}
      {v.webp ? <source srcSet={v.webp} type="image/webp" /> : null}
      <img src={src} alt={alt} {...imgProps} />
    </picture>
  );
}
